import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useShallow } from 'zustand/react/shallow';
import { surfaceDisplayStatus, type SurfaceEntry, type SurfaceSource } from '@/lib/sync/document-surfaces';
import { isTerminal, runsForEntity, useTemplateRunStore, type PendingStage } from '@/stores/use-template-run-store';
import {
  isContractViolation, isServerError, isStartRefusal, startRefusalMessage, startTemplateRun,
} from '@/lib/templates/api';
import { SUMMARY_TEMPLATE_ID } from '@/lib/templates/catalog';
import { useTemplates } from '@/lib/templates/use-templates-store';
import { recordedSource, type TemplateSourceOption } from '../utils/template-sources';
import { recordedSources, sourceTitles } from '../utils/recorded-sources';
import { placeholderFor, runsAwaitingSurface } from '../utils/placeholder-match';
import { isAtSurfaceCap } from '../utils/surface-cap';
import type { PendingSurfaceRow } from '../components/surface-pending-row';

function markSurfaceOpened(surfaceId: string | null) {
  if (surfaceId === null) return;
  const { runs, markOpened } = useTemplateRunStore.getState();
  for (const run of Object.values(runs)) {
    if (run.surfaceId === surfaceId && !run.opened) markOpened(run.runId);
  }
}

export interface SummarizeOffer {
  busy: boolean;
  refusal: string | null;
  onSummarize: (recordingId: string) => void;
}

interface UseTemplateRunsOptions {
  entityId: string;
  entries: SurfaceEntry[];
  /** The document's title, for the refusal that names it and the copy with no recorded sources. */
  sourceTitle: string;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  /** The panel is closed or showing the surfaces list: the only places a new
   *  surface may open itself (ui-design.md §4.1). */
  listShowing: boolean;
  panelOpen: boolean;
  onOpenPanel: () => void;
  onSelect: (id: string) => void;
  onReturnToList: () => void;
  onRemoveSurface: (id: string) => void;
  onOpenTemplate: (templateId: string, sources: readonly SurfaceSource[] | null) => void;
}

export interface TemplateRuns {
  /** Activates a surface as the person's own choice, which a pending run's
   *  new surface must not override. */
  activate: (id: string | null) => void;
  /** The ready surface generated here while the person was away, marked
   *  opened, or null (ui-design.md §5.4). */
  claimArrivedSurface: () => string | null;
  pendingRows: PendingSurfaceRow[];
  matchedRunStage: PendingStage | null;
  templateBusy: boolean;
  templateRefusal: string | null;
  clearRefusal: () => void;
  generate: (templateId: string, sources: readonly TemplateSourceOption[]) => void;
  /** The offer to summarize a recording surface, from its recording's source
   *  options, or undefined once a summary has read that recording (ui-design.md §3.4). */
  summaryOffer: (
    surface: SurfaceEntry,
    recordingId: string | null,
    recordingOptions: readonly TemplateSourceOption[],
  ) => SummarizeOffer | undefined;
  tryAgain: (surfaceId: string, templateId: string) => void;
  retireRunsFor: (surfaceId: string) => void;
  sourceTitlesFor: (entry: SurfaceEntry) => string[];
  removeRun: (runId: string) => void;
}

export function useTemplateRuns({
  entityId,
  entries,
  sourceTitle,
  activeId,
  setActiveId,
  listShowing,
  panelOpen,
  onOpenPanel,
  onSelect,
  onReturnToList,
  onRemoveSurface,
  onOpenTemplate,
}: UseTemplateRunsOptions): TemplateRuns {
  const templates = useTemplates();
  const markOpened = useTemplateRunStore((s) => s.markOpened);
  const entityRuns = useTemplateRunStore(useShallow((s) => runsForEntity(s.runs, entityId)));
  const addRun = useTemplateRunStore((s) => s.addRun);
  const matchSurface = useTemplateRunStore((s) => s.matchSurface);
  const removeRun = useTemplateRunStore((s) => s.removeRun);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateRefusal, setTemplateRefusal] = useState<string | null>(null);
  const [summarizing, setSummarizing] = useState<string | null>(null);
  const [summaryRefusal, setSummaryRefusal] = useState<{ surfaceId: string; message: string } | null>(null);
  if (summaryRefusal !== null && summaryRefusal.surfaceId !== activeId) setSummaryRefusal(null);
  const [retry, setRetry] = useState<{ templateId: string; sources: readonly SurfaceSource[] | null } | null>(null);
  // Counts the person's own surface activations. Each run keeps the count and
  // the active surface from when its Generate was pressed; a run missing here
  // was started by another mount, so its surface never opens itself.
  const activationsRef = useRef(0);
  const generatedFromRef = useRef<Record<string, { activeId: string | null; activations: number }>>({});
  const startingRef = useRef(new Set<string>());

  const activate = useCallback((id: string | null) => {
    activationsRef.current += 1;
    markSurfaceOpened(id);
    setActiveId(id);
  }, [setActiveId]);

  const claimArrivedSurface = () => {
    const arrived = entityRuns.find((run) => run.stage === 'ready' && !run.opened
      && entries.some((e) => e.id === run.surfaceId));
    if (!arrived?.surfaceId) return null;
    markOpened(arrived.runId);
    return arrived.surfaceId;
  };

  const pendingRows: PendingSurfaceRow[] = runsAwaitingSurface(entityRuns, entries)
    .map((run) => ({
      runId: run.runId,
      name: run.templateName,
      surface: run.outputType,
      couldNotStart: isTerminal(run.stage),
    }));

  const activeEntryId = entries.some((e) => e.id === activeId) ? activeId : null;
  const matchedRunStage = useTemplateRunStore((s) => {
    if (activeEntryId === null) return null;
    const matched = Object.values(s.runs).filter((run) => run.surfaceId === activeEntryId);
    return (matched.find((run) => !isTerminal(run.stage)) ?? matched[0])?.stage ?? null;
  });

  useEffect(() => {
    const claimed = new Set<string | null>(entityRuns.map((run) => run.surfaceId));
    for (const run of entityRuns) {
      const placeholder = placeholderFor(run, entries, claimed);
      if (!placeholder) continue;
      claimed.add(placeholder.id);
      matchSurface(run.runId, placeholder.id);
    }
  }, [entries, entityRuns, matchSurface]);

  // ui-design.md §4.1: the new surface opens itself only if the person is still
  // where they pressed Generate. It never moves focus (§7).
  useEffect(() => {
    for (const run of entityRuns) {
      if (run.surfaceId === null && isTerminal(run.stage)) {
        delete generatedFromRef.current[run.runId];
        continue;
      }
      if (run.opened || run.surfaceId === null || !entries.some((e) => e.id === run.surfaceId)) {
        continue;
      }
      const from = generatedFromRef.current[run.runId];
      delete generatedFromRef.current[run.runId];
      const stillThere = from !== undefined
        && from.activeId === activeId
        && from.activations === activationsRef.current;
      if (stillThere && listShowing) {
        markOpened(run.runId);
        setActiveId(run.surfaceId);
      }
    }
  }, [entries, entityRuns, activeId, listShowing, markOpened, setActiveId]);

  interface StartOptions {
    setBusy: (busy: boolean) => void;
    refuse: (message: string) => void;
    refusalTitle: string;
    onAccepted: () => void;
  }

  const start = (id: string, sources: readonly TemplateSourceOption[], how: StartOptions) => {
    const template = templates?.find((t) => t.id === id);
    if (!template) return;
    const running = entityRuns.find((run) => run.templateId === id && !isTerminal(run.stage));
    const generating = entries.find((e) =>
      (e.generator === id && surfaceDisplayStatus(e) === 'generating') || e.id === running?.surfaceId);
    if (generating) {
      onSelect(generating.id);
      return;
    }
    if (running) {
      onReturnToList();
      return;
    }
    if (startingRef.current.has(id)) return;
    startingRef.current.add(id);
    how.setBusy(true);
    const knownSurfaceIds = entries.map((e) => e.id);
    const generatedFrom = { activeId, activations: activationsRef.current };
    void startTemplateRun(id, {
      sources: sources.map((option) => option.request),
      target: { kind: 'new-surface', entityId },
    })
      .then(
        ({ runId }) => {
          generatedFromRef.current[runId] = generatedFrom;
          addRun({
            runId, templateId: id, templateName: template.name, outputType: template.outputType,
            entityId, knownSurfaceIds,
            sources: sources.map(recordedSource),
          });
          how.onAccepted();
        },
        (error: unknown) => {
          if (isServerError(error) || isContractViolation(error)) {
            Sentry.captureException(error);
            console.error('template_run_start_failed', error);
          } else if (!isStartRefusal(error)) {
            console.warn('template_run_start_failed', error);
          }
          how.refuse(startRefusalMessage(error, how.refusalTitle));
        },
      )
      .catch((error: unknown) => {
        Sentry.captureException(error);
        console.error('template_run_start_failed', error);
        how.refuse(startRefusalMessage(error, how.refusalTitle));
      })
      .finally(() => {
        startingRef.current.delete(id);
        how.setBusy(false);
      });
  };

  const generate = (id: string, sources: readonly TemplateSourceOption[]) => {
    start(id, sources, {
      setBusy: setTemplateBusy,
      refuse: setTemplateRefusal,
      refusalTitle: sourceTitle,
      onAccepted: onReturnToList,
    });
  };

  const hasSummaryOf = (recordingId: string | null) => {
    if (recordingId === null) return false;
    const reads = (sources: readonly SurfaceSource[] | undefined) =>
      (sources ?? []).some((s) => s.kind === 'transcript' && s.recordingId === recordingId);
    return entries.some((e) => e.generator === SUMMARY_TEMPLATE_ID && surfaceDisplayStatus(e) !== 'failed' && reads(e.sources))
      || entityRuns.some((run) => run.templateId === SUMMARY_TEMPLATE_ID && !isTerminal(run.stage) && reads(run.sources));
  };

  const atCap = isAtSurfaceCap(entries.length, pendingRows);
  const summaryListed = !!templates?.some((t) => t.id === SUMMARY_TEMPLATE_ID);

  const summaryOffer = (
    { id: surfaceId }: SurfaceEntry,
    recordingId: string | null,
    recordingOptions: readonly TemplateSourceOption[],
  ): SummarizeOffer | undefined => (summaryListed && !hasSummaryOf(recordingId) && !atCap ? {
    busy: summarizing === surfaceId,
    refusal: summaryRefusal?.message ?? null,
    onSummarize: (recordingId) => {
      const option = recordingOptions.find((o) => o.id === `recording:${recordingId}`);
      if (!option) return;
      start(SUMMARY_TEMPLATE_ID, [option], {
        setBusy: (busy) => setSummarizing(busy ? surfaceId : null),
        refuse: (message) => setSummaryRefusal({ surfaceId, message }),
        refusalTitle: option.name,
        onAccepted: () => {},
      });
    },
  } : undefined);

  const retireRunsFor = (surfaceId: string) => {
    for (const run of entityRuns) {
      if (run.surfaceId === surfaceId) removeRun(run.runId);
    }
  };

  const tryAgain = (surfaceId: string, retryId: string) => {
    const failed = entries.find((e) => e.id === surfaceId);
    const sources = failed ? recordedSources(failed, entityRuns) : null;
    onRemoveSurface(surfaceId);
    setRetry({ templateId: retryId, sources });
    onOpenPanel();
  };

  // The face waits for the panel to be open: FocusTrap focuses the panel's first
  // control when it activates, which would take focus off Generate if the
  // template face mounted in the same commit.
  useEffect(() => {
    if (!panelOpen || retry === null) return;
    setRetry(null);
    setTemplateRefusal(null);
    onOpenTemplate(retry.templateId, retry.sources);
  }, [panelOpen, retry, onOpenTemplate]);

  return {
    activate,
    claimArrivedSurface,
    pendingRows,
    matchedRunStage,
    templateBusy,
    templateRefusal,
    clearRefusal: () => setTemplateRefusal(null),
    generate,
    summaryOffer,
    tryAgain,
    retireRunsFor,
    sourceTitlesFor: (entry) => sourceTitles(entry, entityRuns, sourceTitle),
    removeRun,
  };
}
